import { AsyncPipe, CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  Observable,
  of,
  onErrorResumeNextWith,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs';

import { NotificationService } from '@blaze-shared/platform-data-access';
import {
  ButtonBarComponent,
  ButtonIconComponent,
  DialogComponent,
  ScrollerComponent,
} from '@blaze-shared/shared-ui';
import { deepClone, isEmptyish } from '@blaze-shared/shared-util';

import { ApiBuilderStore } from '../+state/api-builder.store';
import {
  API_BUILDER_PARAMETERS_PATTERN,
  ApiBuilderAction,
  ApiBuilderParameterExtended,
  ParameterType,
} from '../api-builder.model';
import { BodyParameterEditDialogComponent } from '../body-parameter-edit-dialog/body-parameter-edit-dialog.component';
import { ApiBuilderService } from '../services/api-builder.service';

interface HoveredLineStyles {
  'top.px': number;
  visibility: string;
}

export interface BodyEditorDialogData {
  action: ApiBuilderAction;
}

/**
 * Used to calculate number of lines.
 * Should match editor line height.
 */
const EDITOR_LINE_HEIGHT_PX = 24;

@UntilDestroy()
@Component({
  selector: 'blaze-body-editor-dialog',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    ButtonIconComponent,
    CommonModule,
    DialogComponent,
    MatIconModule,
    ScrollerComponent,
  ],
  templateUrl: './body-editor-dialog.component.html',
  styleUrls: ['./body-editor-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BodyEditorDialogComponent implements OnInit, AfterViewInit {
  private readonly apiBuilderService = inject(ApiBuilderService);
  private readonly apiBuilderStore = inject(ApiBuilderStore);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly dialogData = inject<BodyEditorDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<BodyEditorDialogComponent>>(MatDialogRef);
  private readonly notificationService = inject(NotificationService);

  @ViewChild('editorContent') editorContent!: ElementRef<HTMLDivElement>;

  action = this.dialogData.action;
  parameters: ApiBuilderParameterExtended[] = [];
  highlightedLineStyles$$ = new BehaviorSubject<HoveredLineStyles | null>(null);
  highlightedLineStyles$ = this.highlightedLineStyles$$.asObservable();
  createContainersRegExp = new RegExp(
    `(?<whitespace>:\\s*)(?:(?<dynamicParam>\\$[\\w-]+)|(?<extendedParam>\\\${(:?${API_BUILDER_PARAMETERS_PATTERN})\\((?<name>[\\w-]+)\\s*(,\\s*(?<param2>[\\w- ]*))?\\s*(,\\s*(?<param3>[\\w-]*))?\\)})|(?<jsonValue>".*?"|\\d*\\.?\\d+|true|false|null))`,
    'g',
  );
  decompileBodyRegExp = new RegExp(
    `"\\\${(?<type>${API_BUILDER_PARAMETERS_PATTERN})\\((?<name>[\\w-]+)\\s*(,\\s*(?<param2>[\\w-. ]*))?\\s*(,\\s*(?<param3>[\\w-]*))?\\)}"`,
    'g',
  );
  compileBodyRegExp = new RegExp(
    `(\\$(?<simpleParam>[\\w-]+))|(\\\${(?<type>${API_BUILDER_PARAMETERS_PATTERN})\\((?<name>[\\w-]+)\\s*(,\\s*(?<param2>[\\w-. ]*))?\\s*(,\\s*(?<param3>[\\w-]*))?\\)})`,
    'g',
  );
  findParameterRegExp =
    /(\$(?<dynamicParamName>\w+))|(\((?<paramName>\w+)\s*(,\s*([\w-. ]*))?\s*(,\s*([\w-]*))?\))/;

  linesCount = 1;

  ngOnInit(): void {
    this.parameters = deepClone(this.action.data.bodyParameters);
  }

  ngAfterViewInit(): void {
    this.editorContent.nativeElement.innerHTML = this.createParameterContainers(
      this.decompileBodyToView(this.action.data.body),
    );

    /**
     * Assign buttons to JSON values in editor.
     */
    fromEvent(this.editorContent.nativeElement, 'input')
      .pipe(
        untilDestroyed(this),
        debounceTime(200),
        map(() => this.getBodyTextValue()),
        map((value) => ({
          html: this.createParameterContainers(value),
          caret: this.getCaretPosition(this.editorContent.nativeElement),
        })),
      )
      .subscribe(({ html, caret }) => {
        this.editorContent.nativeElement.innerHTML = html;
        this.setCaretPosition(caret, this.editorContent.nativeElement);
      });

    /**
     * React on clicks in editor values.
     */
    fromEvent(this.editorContent.nativeElement, 'click')
      .pipe(
        untilDestroyed(this),
        map((event) => event.target as HTMLSpanElement),
        filter((element) => element.classList.contains('tag')),
        switchMap((element) =>
          this.openParameterEditDialog(element).pipe(
            filter((parameter): parameter is ApiBuilderParameterExtended => !!parameter),
            map((parameter) => ({ element, parameter })),
          ),
        ),
      )
      .subscribe(({ element, parameter }) => {
        this.updateParameterTag(element, parameter);
      });

    /**
     * Update number of lines displayed in left gutter.
     */
    fromEvent(this.editorContent.nativeElement, 'input')
      .pipe(
        startWith(null),
        untilDestroyed(this),
        map(() => this.getLinesCount(this.editorContent.nativeElement)),
        distinctUntilChanged(),
      )
      .subscribe((count) => {
        this.linesCount = count;
        this.cdr.detectChanges();
      });

    fromEvent(this.editorContent.nativeElement, 'mouseenter')
      .pipe(
        untilDestroyed(this),
        switchMap(() =>
          fromEvent<MouseEvent>(this.editorContent.nativeElement, 'mousemove').pipe(
            map((event) => {
              const offsetY = Math.max(
                0,
                Math.floor((event.offsetY - 1) / EDITOR_LINE_HEIGHT_PX) * EDITOR_LINE_HEIGHT_PX,
              );
              return { 'top.px': offsetY, visibility: 'visible' };
            }),
            takeUntil(fromEvent(this.editorContent.nativeElement, 'mouseleave')),
            onErrorResumeNextWith(of(null)),
          ),
        ),
      )
      .subscribe((styles) => {
        this.highlightedLineStyles$$.next(styles);
      });
  }

  /**
   * Lines count is calculated by editor's height divided by line height.
   * @param element
   */
  getLinesCount(element: HTMLElement): number {
    const contentHeight = element.scrollHeight;
    return Math.round(contentHeight / EDITOR_LINE_HEIGHT_PX);
  }

  getBodyTextValue(): string {
    return this.editorContent.nativeElement.innerText;
  }

  getCaretPosition(element: HTMLElement): number {
    const range = window.getSelection()?.getRangeAt(0);
    if (!range) {
      return 0;
    }

    const clone = range.cloneRange();
    clone.selectNodeContents(element);
    clone.setEnd(range.endContainer, range.endOffset);
    return clone.toString().length;
  }

  setCaretPosition(position: number, parent: Node): number {
    /**
     * Using for loop for performance issues
     */
    for (let i = 0; i < parent.childNodes.length; i += 1) {
      const node = parent.childNodes[i];
      if (node.nodeValue && node.nodeType === Node.TEXT_NODE) {
        if (node.nodeValue.length >= position) {
          const range = document.createRange();
          const sel = window.getSelection();
          range.setStart(node, position);
          range.collapse(true);
          sel?.removeAllRanges();
          sel?.addRange(range);
          return -1;
        } else {
          position = position - node.nodeValue.length;
        }
      } else {
        position = this.setCaretPosition(position, node);
        if (position < 0) {
          return position;
        }
      }
    }

    return position;
  }

  /**
   * Replaces tags used in backend to tags used in frontend
   * "${Parameter(<name>)}" -> $<name>
   * "${Parameter(<name>,<param2>)}" -> ${Parameter(<name>,<dataType>)}
   * "${ParameterOptional(<name>,<param2>,<param3>)}" -> ${ParameterOptional(<name>,<defaultValue>,<dataType>)}
   * "${Pagination(<type>)}" -> ${Pagination(<type>)}
   * @param body
   */
  decompileBodyToView(body: any): string {
    if (!body) {
      return '';
    }

    const toParse = JSON.stringify(body, null, 4);
    const replaced = toParse.replace(this.decompileBodyRegExp, (...matches) => {
      const groups = matches[matches.length - 1];
      const { name, type, param2, param3 } = groups || {};

      if (type === 'Parameter' && !param2) {
        return `$${name}`;
      }

      return `\${${type}(${this.resolveParamArgs(name, param2, param3)})}`;
    });

    return replaced;
  }

  /**
   * Replaces tags used in view to tags used in backend
   * $<name> -> "${Parameter(<name>)}"
   * ${Parameter|ParameterOptional|Pagination(<name>[,<param2>][,<param3>])} -> "${<type>(<name>[,<param2>][,<param3>])}"
   * @param view
   */
  compileViewToBody(view: string): string {
    return view.replace(this.compileBodyRegExp, (...matches) => {
      const groups = matches[matches.length - 1];
      const { simpleParam, name, type, param2, param3 } = groups || {};

      if (simpleParam) {
        return `"\${Parameter(${simpleParam})}"`;
      }

      return `"\${${type}(${this.resolveParamArgs(name, param2, param3)})}"`;
    });
  }

  /**
   * Find all JSON values and created parameter fields then wrap them in spans.
   * @param value
   */
  createParameterContainers(value: string): string {
    return value.replace(this.createContainersRegExp, (...matches) => {
      const groups = matches[matches.length - 1];
      const { whitespace, dynamicParam, extendedParam, jsonValue } = groups || {};
      const className = dynamicParam || extendedParam ? 'tag parameter' : 'tag value';
      const displayValue = extendedParam || dynamicParam || jsonValue || '';

      return `${whitespace}<span class="${className}">${displayValue}</span>`;
    });
  }

  openParameterEditDialog(
    element?: HTMLElement,
  ): Observable<Partial<ApiBuilderParameterExtended> | undefined> {
    const parameter = this.findParameterByElement(element) || {
      name: '',
      value: '',
      type: ParameterType.Dynamic,
    };

    const dialogRef = this.dialog.open(BodyParameterEditDialogComponent, {
      data: { parameter },
      width: '375px',
      height: 'auto',
      hasBackdrop: true,
      panelClass: ['bd', 'bd-dialog'],
    });

    return dialogRef.afterClosed();
  }

  updateParameterTag(element: HTMLElement, parameter: ApiBuilderParameterExtended): void {
    element.className = 'tag parameter';
    if (parameter.type === ParameterType.Dynamic && !parameter.optional && !parameter.dataType) {
      element.innerText = `$${parameter.name}`;
      return;
    }
    const p = this.apiBuilderService.convertExtendedToParameter(parameter);
    element.innerText = p?.value || '';
  }

  findParameterByElement(element?: HTMLElement): ApiBuilderParameterExtended | undefined {
    if (!element?.innerText) {
      return undefined;
    }

    let text = element.innerText || '';
    try {
      text = String(JSON.parse(text));
    } catch {
      /* noop */
    }
    const match = this.findParameterRegExp.exec(text);
    const { dynamicParamName = '', paramName = '' } = match?.groups || {};
    const name = dynamicParamName || paramName;
    const value = dynamicParamName ? `\${Parameter(${dynamicParamName})}` : text;

    return this.apiBuilderService.convertParameterToExtended({ name, value });
  }

  /**
   * Clean pasted text from styles.
   */
  onTextPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain');
    document.execCommand('insertHTML', false, text);
  }

  focusEditorContent(): void {
    this.editorContent.nativeElement.focus();
  }

  save(): void {
    const bodyText = this.getBodyTextValue();

    let body;
    try {
      body = bodyText ? JSON.parse(this.compileViewToBody(bodyText)) : null;
    } catch {
      this.notificationService.error('Invalid JSON structure!');
      return;
    }

    this.apiBuilderStore.updateAction({
      ...this.action,
      data: {
        ...this.action.data,
        body,
        bodyParameters: this.apiBuilderService.getParametersFromActionBody(body),
      },
    });
    this.returnToBuilder();
  }

  returnToBuilder(): void {
    this.dialogRef.close();
  }

  private resolveParamArgs(name = '', param2 = '', param3 = ''): string {
    param2 = param2?.trim();
    let paramArgs: (string | undefined)[];
    if (param3) {
      paramArgs = [name, param2, param3];
    } else if (!isEmptyish(param2, { emptyString: true })) {
      paramArgs = [name, param2];
    } else {
      paramArgs = [name];
    }

    return paramArgs.join(',');
  }
}
